import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import {
  Box,
  Typography,
  TextField,
  Stack,
  Button,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { useAppDispatch } from '@/hooks';
import { getAuth, sendEmailVerification } from 'firebase/auth';
import { login } from '@/features/auth/slice';
import { ArrowForward, Visibility, VisibilityOff } from '@mui/icons-material';
import styled from 'styled-components';
import AppConfig from '@/components/AppConfig';
import { Configs } from '@/constants/configs';
import routes from '@/constants/routes';

export const Divider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding-top: 50px;
  padding-bottom: 50px;

  &::after,
  &::before {
    border-top: 2px solid lightGrey;
    content: '';
    width: 100px;
  }
`;

const SignInForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [userNotVerified, setUserNotVerified] = useState(null);
  const [alert, setAlert] = useState({
    message: 'Correo no verificado',
  });
  const [showPassword, setShowPassword] = useState(false);

  const signInSchema = Yup.object().shape({
    email: Yup.string().email('Correo no válido').required('Campo requerido'),
    password: Yup.string()
      .min(2, 'Muy corto')
      .max(30, 'Muy Largo')
      .required('Campo requerido'),
  });

  const formik = useFormik({
    initialValues: { email: '', password: '' },
    validationSchema: signInSchema,
    onSubmit: async ({ email, password }) => {
      const response = await dispatch(login({ email, password }));

      if (response?.payload?.emailVerified) {
        setUserNotVerified(response.payload?.email);
        navigate(location.state.prevPage);
      } else {
        if (!response.payload?.emailVerified) {
          setUserNotVerified(response.payload?.email);
        }
      }
    },
  });

  const verifyEmail = async () => {
    try {
      const auth = getAuth();
      if (auth) {
        await sendEmailVerification(auth.currentUser);
        setUserNotVerified(null);
      }
    } catch (error) {
      console.error(error);
      if (error?.code === 'auth/too-many-requests') {
        setAlert({
          message: 'Demasiados intentos, prueba mas tarde.',
        });
      }
    }
  };

  return (
    <Box>
      <form onSubmit={formik.handleSubmit}>
        <Stack
          spacing={3}
          sx={{
            display: 'flex',
            flex: 1,
          }}
        >
          <Typography variant="h2">Iniciar Sesión</Typography>
          {userNotVerified ? (
            <Alert
              severity="warning"
              action={
                <Button size="small" onClick={verifyEmail}>
                  Verificar
                </Button>
              }
            >
              {alert.message}
            </Alert>
          ) : null}
          <Stack spacing={2}>
            <TextField
              fullWidth
              name="email"
              type="email"
              label="Correo"
              margin="normal"
              value={formik.values.email}
              onChange={formik.handleChange}
            />
            <TextField
              name="password"
              label="Contraseña"
              type={showPassword ? 'text' : 'password'}
              margin="normal"
              value={formik.values.password}
              onChange={formik.handleChange}
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              size="large"
              variant="contained"
              type="submit"
              endIcon={<ArrowForward />}
            >
              Iniciar Sesión
            </Button>
            <AppConfig isEnabled={Configs.ACCOUNT_CREATION}>
              <Button
                size="large"
                variant="text"
                onClick={() => navigate(routes.signUp)}
              >
                Crear Cuenta
              </Button>
              <Divider>ó</Divider>
              <Button
                size="large"
                variant="outlined"
                onClick={() => window.alert('Test')}
              >
                Google
              </Button>
              <Button
                size="large"
                variant="outlined"
                onClick={() => window.alert('Test')}
              >
                Facebook
              </Button>
            </AppConfig>
          </Stack>
        </Stack>
      </form>
    </Box>
  );
};

export default SignInForm;
